import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import SearchIcon from "@mui/icons-material/Search";
import SchemaIcon from "@mui/icons-material/Schema";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { SUBSCRIPTION_PLANS_VENDORS } from "../constants/admin";
import { useApi } from "../hooks/useApi";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function AdminSubscriptionPlans() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [activeFilter, setActiveFilter] = React.useState("");
  const [vendorFilter, setVendorFilter] = React.useState("");
  const [sortBy, setSortBy] = React.useState("created_at");
  const [sortOrder, setSortOrder] = React.useState("desc");
  const [pagination, setPagination] = React.useState({
    total: 0,
    limit: 20,
    offset: 0,
    page: 1,
  });
  const [schemaDialogOpen, setSchemaDialogOpen] = React.useState(false);
  const [schemaTableName, setSchemaTableName] = React.useState("");
  const [schemaData, setSchemaData] = React.useState(null);
  const [loadingSchema, setLoadingSchema] = React.useState(false);
  const [schemaError, setSchemaError] = React.useState(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["subscription-plans", debouncedSearch, activeFilter, vendorFilter, sortBy, sortOrder, pagination.offset, pagination.limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch?.trim()) params.append("search", debouncedSearch.trim());
      if (activeFilter) params.append("active", activeFilter);
      if (vendorFilter?.trim()) params.append("vendor", vendorFilter.trim());
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder);
      params.append("limit", pagination.limit.toString());
      params.append("offset", pagination.offset.toString());
      return api.get(`/admin/subscription-plans?${params.toString()}`);
    },
  });

  const plans = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const loading = isLoading;

  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * pagination.limit;
    setPagination((prev) => ({ ...prev, offset: newOffset, page: newPage }));
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({ ...prev, limit: newLimit, offset: 0, page: 1 }));
  };

  const totalPages = Math.ceil(total / pagination.limit);

  const formatPrice = (price) => {
    if (price === null || price === undefined) return "N/A";
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const handleCloseSchemaDialog = () => {
    setSchemaDialogOpen(false);
    setSchemaTableName("");
    setSchemaData(null);
    setSchemaError(null);
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Box sx={{ px: 3, pt: 1, pb: 1 }}>
            <Breadcrumbs aria-label="breadcrumb">
              <Typography
                color="primary"
                onClick={() => navigate("/dashboard")}
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Admin
              </Typography>
              <Typography color="text.primary" fontWeight={500}>
                Subscription Plans
              </Typography>
            </Breadcrumbs>
          </Box>
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <AdminPageHeader
              title="Subscription Plans"
              description="Manage subscription plan templates"
              icon={<CardMembershipIcon sx={{ color: "primary.main" }} />}
            >
              <Button
                variant="contained"
                sx={{ bgcolor: "white", color: "black", "&:hover": { bgcolor: "grey.100" } }}
                startIcon={<SchemaIcon />}
                onClick={async () => {
                  setSchemaDialogOpen(true);
                  setSchemaTableName("subscription_plans");
                  setSchemaData(null);
                  setSchemaError(null);
                  setLoadingSchema(true);
                  setSchemaError(null);
                  try {
                    const data = await api.get("/admin/schema/subscription_plans");
                    setSchemaData(data);
                  } catch (err) {
                    console.error("Error fetching schema:", err);
                    setSchemaError(err.message || "Failed to fetch schema");
                  } finally {
                    setLoadingSchema(false);
                  }
                }}
              >
                Schema
              </Button>
            </AdminPageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.message || "Failed to fetch subscription plans"}
              </Alert>
            )}

            <Card>
              <CardContent>
                {/* Top pagination controls */}
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Skeleton height={20} width={180} />
                      <Skeleton height={40} width={120} borderRadius={1} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Skeleton height={32} width={80} borderRadius={1} />
                      <Skeleton height={20} width={150} />
                      <Skeleton height={32} width={60} borderRadius={1} />
                    </Box>
                  </Box>
                ) : total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, total)} of {total} plans
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1 || loading}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Page {pagination.page} of {totalPages} ({total} total)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= totalPages || loading}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
                
                <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search plans by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={activeFilter}
                      onChange={(e) => setActiveFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="true">Active</MenuItem>
                      <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Vendor</InputLabel>
                    <Select
                      value={vendorFilter}
                      onChange={(e) => setVendorFilter(e.target.value)}
                      label="Vendor"
                    >
                      <MenuItem value="">All</MenuItem>
                      {SUBSCRIPTION_PLANS_VENDORS.map((v) => (
                        <MenuItem key={v} value={v}>
                          {v}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Sort by</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => {
                        setSortBy(e.target.value);
                        setPagination((prev) => ({ ...prev, offset: 0, page: 1 }));
                      }}
                      label="Sort by"
                    >
                      <MenuItem value="name">Plan Name</MenuItem>
                      <MenuItem value="term">Term</MenuItem>
                      <MenuItem value="price">Price</MenuItem>
                      <MenuItem value="active">Status</MenuItem>
                      <MenuItem value="vendor">Vendor</MenuItem>
                      <MenuItem value="created_at">Created</MenuItem>
                      <MenuItem value="updated_at">Updated</MenuItem>
                      <MenuItem value="replaced_by_plan_name">Replaced By</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Order</InputLabel>
                    <Select
                      value={sortOrder}
                      onChange={(e) => {
                        setSortOrder(e.target.value);
                        setPagination((prev) => ({ ...prev, offset: 0, page: 1 }));
                      }}
                      label="Order"
                    >
                      <MenuItem value="asc">Ascending</MenuItem>
                      <MenuItem value="desc">Descending</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Plan Name</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Term</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Replaced By</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        Array.from({ length: 20 }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={80} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={24} width={80} borderRadius={4} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={120} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton height={32} width={80} />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : plans.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography color="text.secondary">
                              {searchQuery || activeFilter || vendorFilter ? "No subscription plans found matching your filters" : "No subscription plans found"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        plans.map((plan) => (
                          <TableRow key={plan.id}>
                            <TableCell>{plan.name || "N/A"}</TableCell>
                            <TableCell>{plan.vendor || "N/A"}</TableCell>
                            <TableCell>{plan.term || "N/A"}</TableCell>
                            <TableCell>{formatPrice(plan.price)}</TableCell>
                            <TableCell>
                              <Chip
                                label={plan.active ? "Active" : "Inactive"}
                                color={plan.active ? "success" : "default"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{plan.replaced_by_plan_name || "N/A"}</TableCell>
                            <TableCell>
                              {plan.created_at
                                ? new Date(plan.created_at).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              {plan.updated_at
                                ? new Date(plan.updated_at).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => navigate(`/dashboard/subscription-plans/${plan.id}`)}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Bottom pagination controls */}
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Skeleton height={20} width={180} />
                      <Skeleton height={40} width={120} borderRadius={1} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Skeleton height={32} width={80} borderRadius={1} />
                      <Skeleton height={20} width={150} />
                      <Skeleton height={32} width={60} borderRadius={1} />
                    </Box>
                  </Box>
                ) : total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, total)} of {total} plans
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1 || loading}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Page {pagination.page} of {totalPages} ({total} total)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= totalPages || loading}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>

      {/* Schema Dialog */}
      <Dialog
        open={schemaDialogOpen}
        onClose={handleCloseSchemaDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Table Schema</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Table Name</InputLabel>
              <Select
                value={schemaTableName}
                onChange={(e) => {
                  setSchemaTableName(e.target.value);
                  // Automatically fetch schema when selection changes
                  if (e.target.value) {
                    const fetchSchema = async () => {
                      setLoadingSchema(true);
                      setSchemaError(null);
                      setSchemaData(null);
                      try {
                        const data = await api.get(`/admin/schema/${e.target.value}`);
                        setSchemaData(data);
                      } catch (err) {
                        console.error("Error fetching schema:", err);
                        setSchemaError(err.message || "Failed to fetch schema");
                      } finally {
                        setLoadingSchema(false);
                      }
                    };
                    fetchSchema();
                  }
                }}
                label="Table Name"
              >
                <MenuItem value="subscription_plans">subscription_plans</MenuItem>
                <MenuItem value="organizations">organizations</MenuItem>
                <MenuItem value="users">users</MenuItem>
                <MenuItem value="subscriptions">subscriptions</MenuItem>
                <MenuItem value="orders">orders</MenuItem>
                <MenuItem value="products">products</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {schemaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {schemaError}
            </Alert>
          )}

          {loadingSchema && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {schemaData && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Table: {schemaData.table_name}
              </Typography>
              
              {/* Columns */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Columns
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Nullable</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Position</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schemaData.columns.map((col) => (
                      <TableRow key={col.name}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={schemaData.primary_keys?.includes(col.name) ? "bold" : "normal"}>
                            {col.name}
                            {schemaData.primary_keys?.includes(col.name) && " (PK)"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {col.type}
                            {col.max_length && `(${col.max_length})`}
                            {col.precision && col.scale && `(${col.precision},${col.scale})`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={col.nullable ? "YES" : "NO"}
                            size="small"
                            color={col.nullable ? "default" : "warning"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                            {col.default || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{col.position}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Foreign Keys */}
              {schemaData.foreign_keys && schemaData.foreign_keys.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Foreign Keys
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Column</TableCell>
                          <TableCell>References Table</TableCell>
                          <TableCell>References Column</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schemaData.foreign_keys.map((fk, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{fk.column}</TableCell>
                            <TableCell>{fk.references_table}</TableCell>
                            <TableCell>{fk.references_column}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Unique Constraints */}
              {schemaData.unique_constraints && schemaData.unique_constraints.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Unique Constraints
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {schemaData.unique_constraints.map((uc, idx) => (
                      <Chip
                        key={idx}
                        label={`${uc.column} (${uc.constraint_name})`}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </>
              )}

              {/* Indexes */}
              {schemaData.indexes && schemaData.indexes.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Indexes
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Definition</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schemaData.indexes.map((idx) => (
                          <TableRow key={idx.name}>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">
                                {idx.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                                {idx.definition}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Raw JSON View */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Raw JSON
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      overflow: "auto",
                      maxHeight: "300px",
                      m: 0,
                    }}
                  >
                    {JSON.stringify(schemaData, null, 2)}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSchemaDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
